import { useEffect, useMemo } from 'react';
import { Plus } from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';

import { GoalsEmptyVision } from '../../components/goals/GoalsEmptyVision';
import { GoalMosaicTile } from '../../components/goals/GoalMosaicTile';
import { GoalsVisionHeader } from '../../components/goals/GoalsVisionHeader';
import { GoalsYearCalendarTeaser } from '../../components/goals/GoalsYearCalendarTeaser';
import { PriorityGoalCard } from '../../components/goals/PriorityGoalCard';
import { Skeleton } from '../../components/ui/Skeleton';
import { useAnnualCalendarQuery } from '../../features/calendar/queries';
import {
  useGoalsQuery,
  usePriorityGoalQuery,
} from '../../features/goals/queries';
import pt from '../../locales/pt';
import {
  StickyPageAction,
  StickyPageActionButton,
} from '../../components/layout/StickyPageAction';
import { MOBILE_PAGE_PADDING_WITH_STICKY_CLASS } from '../../components/layout/mobileLayoutConstants';
import { cn } from '../../utils/cn';
import { PageContentShell } from '../../components/common/PageContentShell';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import {
  goalsForMosaic,
  isActiveGoal,
  resolvePriorityGoal,
} from '../../utils/goalDisplayUtils';
import { paths } from '../../routes/paths';

/** Objetivos · Loading no .pen — espelha hero + mosaico + ano à frente. */
function GoalsLoadingSkeleton() {
  return (
    <div className="flex flex-col gap-space-5" aria-busy="true" aria-label={pt.common.loading}>
      <div className="flex flex-col gap-space-2">
        <Skeleton className="h-3 w-24 rounded-full" />
        <Skeleton className="h-6 w-56 rounded-full" />
        <Skeleton className="h-3 w-40 rounded-full" />
      </div>
      <div className="flex flex-col gap-space-3 rounded-ds-lg border border-border bg-card p-space-5 shadow-sm">
        <div className="flex items-center justify-between">
          <Skeleton className="h-3 w-32 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <Skeleton className="h-8 w-52" />
        <Skeleton className="h-[140px] w-full rounded-ds-md" />
      </div>
      <div className="flex flex-col gap-space-3 rounded-ds-lg border border-border bg-card p-space-5 shadow-sm">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-space-3 py-space-2">
            <Skeleton className="h-10 w-10 shrink-0 rounded-ds-md" />
            <div className="flex min-w-0 flex-1 flex-col gap-space-2">
              <Skeleton className="h-3 w-2/5 rounded-full" />
              <Skeleton className="h-2.5 w-1/4 rounded-full" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-space-3 rounded-ds-lg border border-border bg-card p-space-5 shadow-sm">
        <Skeleton className="h-3 w-32 rounded-full" />
        <Skeleton className="h-14 w-full rounded-ds-sm" />
      </div>
    </div>
  );
}

export function Goals() {
  const { data: goals, isPending, isFetching } = useGoalsQuery();
  const goalsShell = useQueryShellState({ data: goals, isPending, isFetching });
  const { data: suggestedPriority } = usePriorityGoalQuery();
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const { data: annualCalendar } = useAnnualCalendarQuery(currentYear);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.state?.openCreateGoal) return;
    navigate(paths.goalsNew, { replace: true });
  }, [location.state, navigate]);

  const activeGoals = useMemo(
    () => (goals ?? []).filter((goal) => isActiveGoal(goal)),
    [goals],
  );

  const priorityGoal = useMemo(
    () => resolvePriorityGoal(activeGoals, suggestedPriority),
    [activeGoals, suggestedPriority],
  );

  const mosaicGoals = useMemo(
    () => goalsForMosaic(activeGoals, priorityGoal?.id),
    [activeGoals, priorityGoal?.id],
  );

  const openCreate = (prefill = null) => {
    navigate(paths.goalsNew, { state: prefill ? { prefill } : null });
  };

  const handleExampleSelect = (example) => {
    openCreate({
      name: example.name,
      description: example.description,
      measurementType: 'monetary',
      budgetAllocation: example.budget_allocation || 'personal_investment',
    });
  };

  const isEmpty = !goalsShell.showBlockingSkeleton && activeGoals.length === 0;
  const isPopulated = !goalsShell.showBlockingSkeleton && activeGoals.length > 0;

  return (
    <div
      className={cn(
        'mx-auto flex w-full min-w-0 flex-col gap-space-5 overflow-x-hidden',
        isEmpty ? 'max-w-2xl' : null,
        MOBILE_PAGE_PADDING_WITH_STICKY_CLASS,
      )}
    >
      <PageContentShell shells={[goalsShell]} fallback={<GoalsLoadingSkeleton />}>
        {isEmpty ? (
          <div className="flex flex-col gap-space-5">
            <GoalsVisionHeader populated={false} />
            <GoalsEmptyVision onCreate={() => openCreate()} onExampleSelect={handleExampleSelect} />
          </div>
        ) : (
          <div className="flex flex-col gap-space-5">
            <GoalsVisionHeader populated count={activeGoals.length} onCreate={() => openCreate()} />

            <div
              data-testid="goals-board-grid"
              className="grid grid-cols-1 items-start gap-space-5 lg:grid-cols-[1.55fr_1fr]"
            >
              {priorityGoal ? (
                <div className="min-w-0 lg:col-start-1 lg:row-start-1">
                  <PriorityGoalCard goal={priorityGoal} />
                </div>
              ) : null}

              {mosaicGoals.length > 0 ? (
                <div className="min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2">
                  <p className="type-section pb-space-3 text-muted-foreground">
                    {pt.goals.vision.dreamsLabel}
                  </p>
                  <div className="grid grid-cols-1 gap-space-3 md:grid-cols-3 md:gap-space-5 lg:grid-cols-1">
                    {mosaicGoals.map((goal) => (
                      <GoalMosaicTile key={goal.id} goal={goal} />
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="min-w-0 lg:col-start-1 lg:row-start-2">
                <GoalsYearCalendarTeaser
                  goals={activeGoals}
                  annualMonths={annualCalendar?.months ?? []}
                  year={currentYear}
                />
              </div>
            </div>
          </div>
        )}
      </PageContentShell>

      {isPopulated ? (
        <StickyPageAction>
          <StickyPageActionButton onClick={() => openCreate()}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            {pt.goals.new}
          </StickyPageActionButton>
        </StickyPageAction>
      ) : null}
    </div>
  );
}

export default Goals;
